import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { adminSeasonsAPI, adminPlayerHomeBannersAPI } from '../services/adminApi';

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;
const Title = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
`;
const Lead = styled.p`
  color: #6b7280;
  font-size: 0.9rem;
  margin: 0 0 1.25rem 0;
  line-height: 1.5;
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1.1rem;
`;
const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;
const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
  max-width: 100%;
`;
const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
  width: 100%;
  box-sizing: border-box;
`;
const TextArea = styled.textarea`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
  min-height: 100px;
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
`;
const Btn = styled.button`
  background: #043663;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  margin-right: 0.5rem;
  margin-top: 0.25rem;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const BtnGhost = styled(Btn)`
  background: #f1f5f9;
  color: #0f172a;
  border: 1px solid #e2e8f0;
`;
const BtnDanger = styled(Btn)`
  background: #b91c1c;
`;
const Hint = styled.p`
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0.25rem 0 0 0;
`;
const TableWrap = styled.div`
  overflow: auto;
  margin-top: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  th,
  td {
    text-align: left;
    padding: 0.55rem 0.65rem;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: top;
  }
  th {
    background: #f9fafb;
    font-weight: 600;
    color: #374151;
  }
`;
const Badge = styled.span`
  display: inline-block;
  padding: 0.15rem 0.45rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${(p) => p.$bg || '#e5e7eb'};
  color: ${(p) => p.$color || '#374151'};
`;

function toDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(s) {
  if (s == null || String(s).trim() === '') return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

const emptyForm = {
  title: '',
  body: '',
  variant: 'info',
  audience_type: 'tenant_all',
  season_id: '',
  priority: 0,
  is_active: true,
  cta_label: '',
  cta_path: '',
  starts_at: '',
  ends_at: '',
  send_push: false,
};

/**
 * @param {{ canEdit: boolean }} props
 */
export default function AdminPlayerHomeBanners({ canEdit = false }) {
  const { adminUser } = useAdminAuth();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const { data: seasonsRes } = useQuery('admin-seasons-banners', () => adminSeasonsAPI.getAll(), {
    enabled: !!adminUser,
    retry: false,
  });
  const seasons = Array.isArray(seasonsRes?.data) ? seasonsRes.data : [];

  const { data: listRes, isLoading } = useQuery(
    'admin-player-home-banners',
    () => adminPlayerHomeBannersAPI.list(),
    {
      enabled: !!adminUser,
      retry: false,
    }
  );
  const banners = listRes?.data?.banners || [];

  const saveMutation = useMutation(
    async () => {
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        variant: form.variant,
        audience_type: form.audience_type,
        season_id: form.audience_type === 'season_players' ? form.season_id || null : null,
        priority: Number(form.priority) || 0,
        is_active: !!form.is_active,
        cta_label: form.cta_label.trim() || null,
        cta_path: form.cta_path.trim() || null,
        starts_at: fromDatetimeLocalValue(form.starts_at),
        ends_at: fromDatetimeLocalValue(form.ends_at),
      };
      if (!payload.title || !payload.body) {
        throw new Error('Title and body are required');
      }
      if (payload.audience_type === 'season_players' && !payload.season_id) {
        throw new Error('Choose a phase for phase audience');
      }
      if (editingId) {
        return adminPlayerHomeBannersAPI.update(editingId, {
          ...payload,
          send_push: !!form.send_push,
        });
      }
      return adminPlayerHomeBannersAPI.create(payload);
    },
    {
      onSuccess: (res) => {
        if (res?.success) {
          const pushStats = res?.data?.pushStats;
          let msg = editingId ? 'Banner updated' : 'Banner created';
          if (pushStats?.queued) {
            msg += ' — push notifications are being sent to players.';
          } else if (pushStats?.skippedReason === 'scheduled_future') {
            msg += ' — push will not send until the start date (not yet live).';
          } else if (pushStats?.skippedReason === 'inactive_or_scheduled') {
            msg += ' — saved as inactive; no push sent.';
          }
          toast.success(msg);
          queryClient.invalidateQueries('admin-player-home-banners');
          setEditingId(null);
          setForm(emptyForm);
        } else {
          toast.error(res?.message || 'Save failed');
        }
      },
      onError: (e) => {
        toast.error(e?.message || e?.response?.data?.message || 'Save failed');
      },
    }
  );

  const deleteMutation = useMutation((id) => adminPlayerHomeBannersAPI.delete(id), {
    onSuccess: (res, id) => {
      if (res?.success) {
        toast.success('Banner deleted');
        if (editingId === id) {
          setEditingId(null);
          setForm(emptyForm);
        }
        queryClient.invalidateQueries('admin-player-home-banners');
      } else {
        toast.error(res?.message || 'Delete failed');
      }
    },
    onError: (e) => toast.error(e?.response?.data?.message || 'Delete failed'),
  });

  const startEdit = useCallback((b) => {
    setEditingId(b.id);
    setForm({
      title: b.title || '',
      body: b.body || '',
      variant: b.variant || 'info',
      audience_type: b.audience_type || 'tenant_all',
      season_id: b.season_id || '',
      priority: b.priority ?? 0,
      is_active: b.is_active !== false,
      cta_label: b.cta_label || '',
      cta_path: b.cta_path || '',
      starts_at: toDatetimeLocalValue(b.starts_at),
      ends_at: toDatetimeLocalValue(b.ends_at),
      send_push: false,
    });
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setForm(emptyForm);
  }, []);

  const formKey = useMemo(() => editingId || 'new', [editingId]);

  return (
    <Card>
      <Title>Home banners (player dashboard)</Title>
      <Lead>
        Short messages appear on each player&apos;s <strong>home</strong> screen under their profile and above performance
        &amp; schedule. They respect scheduling dates, can target everyone in the league or only players registered for a
        specific phase (active team roster), and players can dismiss individual banners for themselves.
        <br />
        <br />
        <strong>Email:</strong> banners are in-app first. To reach players by email as well, use the{' '}
        <strong>Email</strong> tab and send a broadcast for the same phase or audience when needed (same team-registration
        rules apply for phase-scoped sends). There is no automatic link between a banner row and email yet.
        <br />
        <br />
        Automated reminders (availability, incomplete profile) are mixed in by the app with admin banners by priority.
        <br />
        <br />
        <strong>Push notifications:</strong> when a banner is <strong>active</strong> and within its visible window, the server
        also creates a player notification and sends a mobile push (FCM) to the same audience. Scheduled future start dates
        do not push until you save again after they go live. Use &quot;Send push again&quot; when editing to resend.
      </Lead>

      <Field>
        <Label>{editingId ? 'Edit banner' : 'New banner'}</Label>
        <Input
          key={`${formKey}-title`}
          placeholder="Title"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          disabled={!canEdit}
        />
      </Field>
      <Field>
        <Label>Message (plain text)</Label>
        <TextArea
          key={`${formKey}-body`}
          placeholder="Body"
          value={form.body}
          onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
          disabled={!canEdit}
        />
      </Field>
      <Field>
        <Label>Variant</Label>
        <Select
          value={form.variant}
          onChange={(e) => setForm((f) => ({ ...f, variant: e.target.value }))}
          disabled={!canEdit}
        >
          <option value="info">Info</option>
          <option value="warning">Warning</option>
          <option value="urgent">Urgent</option>
        </Select>
      </Field>
      <Field>
        <Label>Audience</Label>
        <Select
          value={form.audience_type}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              audience_type: e.target.value,
              season_id: e.target.value === 'tenant_all' ? '' : f.season_id,
            }))
          }
          disabled={!canEdit}
        >
          <option value="tenant_all">All players (tenant)</option>
          <option value="season_players">Players on teams registered for a phase</option>
        </Select>
      </Field>
      {form.audience_type === 'season_players' ? (
        <Field>
          <Label>Phase</Label>
          <Select
            value={form.season_id}
            onChange={(e) => setForm((f) => ({ ...f, season_id: e.target.value }))}
            disabled={!canEdit}
          >
            <option value="">Select phase</option>
            {seasons.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name || s.id}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}
      <Field>
        <Label>Priority (higher shows first; urgent/warning variants sort ahead by design)</Label>
        <Input
          type="number"
          value={form.priority}
          onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
          disabled={!canEdit}
        />
      </Field>
      <Field>
        <Label>Optional in-app link</Label>
        <Input
          placeholder="Button label (e.g. Open registration)"
          value={form.cta_label}
          onChange={(e) => setForm((f) => ({ ...f, cta_label: e.target.value }))}
          disabled={!canEdit}
        />
        <Input
          style={{ marginTop: 8 }}
          placeholder="Path (must start with /player/ …)"
          value={form.cta_path}
          onChange={(e) => setForm((f) => ({ ...f, cta_path: e.target.value }))}
          disabled={!canEdit}
        />
        <Hint>Both label and path are required for a button to appear.</Hint>
      </Field>
      <Field>
        <Label>Visible from (optional, local time)</Label>
        <Input
          type="datetime-local"
          value={form.starts_at}
          onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))}
          disabled={!canEdit}
        />
      </Field>
      <Field>
        <Label>Visible until (optional, local time)</Label>
        <Input
          type="datetime-local"
          value={form.ends_at}
          onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))}
          disabled={!canEdit}
        />
      </Field>
      <Field>
        <Label>
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
            disabled={!canEdit}
          />{' '}
          Active
        </Label>
      </Field>
      {editingId ? (
        <Field>
          <Label>
            <input
              type="checkbox"
              checked={form.send_push}
              onChange={(e) => setForm((f) => ({ ...f, send_push: e.target.checked }))}
              disabled={!canEdit}
            />{' '}
            Send push again to audience (even if already sent once)
          </Label>
        </Field>
      ) : null}

      <div>
        <Btn type="button" disabled={!canEdit || saveMutation.isLoading} onClick={() => saveMutation.mutate()}>
          {saveMutation.isLoading ? 'Saving…' : editingId ? 'Save changes' : 'Create banner'}
        </Btn>
        {editingId ? (
          <BtnGhost type="button" disabled={!canEdit} onClick={cancelEdit}>
            Cancel edit
          </BtnGhost>
        ) : null}
      </div>
      {!canEdit ? (
        <Hint style={{ marginTop: 12, color: '#b91c1c' }}>
          You do not have permission to edit (requires Settings — edit).
        </Hint>
      ) : null}

      <TableWrap>
        {isLoading ? (
          <p style={{ padding: '1rem', margin: 0 }}>Loading…</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Variant</th>
                <th>Audience</th>
                <th>Window</th>
                <th>Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {banners.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ color: '#6b7280' }}>
                    No banners yet.
                  </td>
                </tr>
              ) : (
                banners.map((b) => (
                  <tr key={b.id}>
                    <td>
                      <strong>{b.title}</strong>
                      <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: 4 }}>
                        {(b.body || '').slice(0, 80)}
                        {(b.body || '').length > 80 ? '…' : ''}
                      </div>
                    </td>
                    <td>
                      <Badge
                        $bg={b.variant === 'urgent' ? '#fee2e2' : b.variant === 'warning' ? '#fef3c7' : '#e0f2fe'}
                        $color={b.variant === 'urgent' ? '#991b1b' : b.variant === 'warning' ? '#92400e' : '#075985'}
                      >
                        {b.variant}
                      </Badge>
                    </td>
                    <td>
                      {b.audience_type === 'tenant_all' ? (
                        'Tenant'
                      ) : (
                        <>
                          Phase
                          <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{b.season_id || '—'}</div>
                        </>
                      )}
                    </td>
                    <td style={{ fontSize: '0.78rem', color: '#475569' }}>
                      {b.starts_at ? new Date(b.starts_at).toLocaleString() : '—'}
                      <br />
                      → {b.ends_at ? new Date(b.ends_at).toLocaleString() : '—'}
                    </td>
                    <td>{b.is_active ? 'Yes' : 'No'}</td>
                    <td>
                      <Btn
                        type="button"
                        style={{ marginTop: 0, padding: '0.35rem 0.65rem', fontSize: '0.8rem' }}
                        disabled={!canEdit}
                        onClick={() => startEdit(b)}
                      >
                        Edit
                      </Btn>
                      <BtnDanger
                        type="button"
                        style={{ marginTop: 0, padding: '0.35rem 0.65rem', fontSize: '0.8rem' }}
                        disabled={!canEdit || deleteMutation.isLoading}
                        onClick={() => {
                          if (window.confirm('Delete this banner? Player dismissals will be removed too.')) {
                            deleteMutation.mutate(b.id);
                          }
                        }}
                      >
                        Delete
                      </BtnDanger>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </TableWrap>
    </Card>
  );
}
